/**
 * HeatmapOverlay.tsx
 *
 * Renders a heatmap.js overlay on top of the Konva Stage, aligned to the
 * pitch viewport. All player samples are projected to canvas pixel coordinates
 * and fed to heatmap.js as weighted data points.
 *
 * Requirements:
 *   npm install heatmap.js
 *   npm install --save-dev @types/heatmap.js   (if available, otherwise see below)
 *
 * Usage:
 *   <ViewportProvider viewport={vp}>
 *     <div style={{ position: "relative" }}>
 *
 *       <Stage width={W} height={H}>
 *         <ViewportBridge>
 *           <Layer> ... </Layer>
 *         </ViewportBridge>
 *       </Stage>
 *
 *       <HeatmapOverlay
 *         players={players}
 *         width={W}
 *         height={H}
 *       />
 *
 *     </div>
 *   </ViewportProvider>
 *
 * The wrapper div must have position: relative so the overlay canvas sits
 * exactly on top of the Konva canvas.
 */

import React, {
  useCallback,
  useEffect, useLayoutEffect,
  useMemo,
  useRef,
} from 'react';
import h337 from "heatmap.js";

import type { ProcessedPlayer, ProcessedSample, Viewport } from "./types";

// ─── TYPES ───────────────────────────────────────────────────────────────────

// heatmap.js does not ship official @types — declare what we need locally.
// If your project already has @types/heatmap.js, remove this block.
type HeatmapInstance = ReturnType<typeof h337.create>;

export interface HeatmapDataPoint {
  x: number;   // canvas px
  y: number;   // canvas px
  value: number;
}

export interface HeatmapOverlayProps {
  /** Processed players from processAllPlayers() */
  players: ProcessedPlayer[];
  /** Canvas width in pixels — must match the Konva Stage width */
  width: number;
  /** Canvas height in pixels — must match the Konva Stage height */
  height: number;
  /**
   * Time window to include.
   * When omitted, ALL samples from every player are used.
   */
  timeRange?: { from: number; to: number };
  /**
   * Which players to include. When omitted, all players are included.
   */
  playerIds?: Array<string | number>;
  /** heatmap.js radius in pixels (default: 30) */
  radius?: number;
  /** heatmap.js max value — controls colour saturation (default: auto) */
  maxValue?: number;
  /** heatmap.js opacity 0–1 (default: 0.6) */
  opacity?: number;
  /** heatmap.js gradient (default: cool → hot) */
  gradient?: Record<string, string>;
  className?: string;
  style?: React.CSSProperties;
  vp?: Viewport;
}

// ─── HELPERS ─────────────────────────────────────────────────────────────────

/** Project a ProcessedSample's canonical {u,v} to canvas pixels. */
function sampleToPixel(
  s: ProcessedSample,
  vp: Viewport,
): { x: number; y: number } {
  return {
    x: Math.round(vp.x + (1 - s.u) * vp.w),   // flip u — matches canonicalToCanvas (ground.ts)
    y: Math.round(vp.y + (1 - s.v) * vp.h),   // flip v — canvas y grows downward
  };
}

/**
 * Build the flat array of { x, y, value } data points from all player samples.
 * Each sample contributes value=1; overlapping points accumulate naturally
 * through heatmap.js's internal bucketing.
 */
function buildHeatmapData(
  players: ProcessedPlayer[],
  vp: Viewport,
  timeRange?: { from: number; to: number },
  playerIds?: Array<string | number>,
): HeatmapDataPoint[] {
  const idSet = playerIds ? new Set(playerIds) : null;

  const points: HeatmapDataPoint[] = [];

  for (const player of players) {
    if (idSet && !idSet.has(player.id)) continue;

    for (const s of player.samples) {
      if (timeRange && (s.t < timeRange.from || s.t > timeRange.to)) continue;

      const { x, y } = sampleToPixel(s, vp);
      points.push({ x, y, value: 1 });
    }
  }

  return points;
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────


const GRADIENT_BLUE_TO_RED_26_STEPS = [
  'rgba(0, 0, 255, 0)',
  'rgba(0, 0, 255, 1)',
  'rgba(0, 31, 255, 1)',
  'rgba(0, 63, 255, 1)',
  'rgba(0, 91, 255, 1)',
  'rgba(0, 127, 255, 1)',
  'rgba(0, 159, 255, 1)',
  'rgba(0, 191, 255, 1)',
  'rgba(0, 223, 255, 1)',
  'rgba(0, 255, 255, 1)',
  'rgba(0, 255, 223, 1)',
  'rgba(0, 255, 191, 1)',
  'rgba(0, 255, 159, 1)',
  'rgba(0, 255, 127, 1)',
  'rgba(0, 255, 91, 1)',
  'rgba(0, 255, 63, 1)',
  'rgba(0, 255, 31, 1)',
  'rgba(0, 255, 0, 1)',
  'rgba(31, 223, 0, 1)',
  'rgba(63, 191, 0, 1)',
  'rgba(91, 159, 0, 1)',
  'rgba(127, 127, 0, 1)',
  'rgba(159, 91, 0, 1)',
  'rgba(191, 63, 0, 1)',
  'rgba(223, 31, 0, 1)',
  'rgba(255, 0, 0, 1)',
];

const DEFAULT_GRADIENT: Record<string, string> = GRADIENT_BLUE_TO_RED_26_STEPS.map((el,id,arr) => ({
  [id / (arr.length - 1)]: el,
})).reduce(
  (acc, el) => ({
    ...acc,
    ...el,
  }),
  {} as Record<string, string>,
);


export function HeatmapOverlay({
  players,
  width,
  height,
  timeRange,
  playerIds,
  radius = 30,
  maxValue,
  opacity = 0.6,
  gradient = DEFAULT_GRADIENT,
  className,
  style,
  vp,
}: HeatmapOverlayProps) {
  const hmRef = useRef<HeatmapInstance | null>(null);

  // Stable refs so the callback ref can always read the latest values
  // without being recreated (which would remount the heatmap).
  const vpRef         = useRef(vp);
  const playersRef    = useRef(players);
  const timeRangeRef  = useRef(timeRange);
  const playerIdsRef  = useRef(playerIds);
  const maxValueRef   = useRef(maxValue);
  vpRef.current        = vp;
  playersRef.current   = players;
  timeRangeRef.current = timeRange;
  playerIdsRef.current = playerIds;
  maxValueRef.current  = maxValue;

  // Config refs so the callback ref can read radius/opacity/gradient without
  // being recreated when they change.
  const radiusRef   = useRef(radius);
  const opacityRef  = useRef(opacity);
  const gradientRef = useRef(gradient);
  radiusRef.current   = radius;
  opacityRef.current  = opacity;
  gradientRef.current = gradient;

  // ── Callback ref — called by React with the real DOM node once it is
  //    attached, and with null when it is detached.
  //    h337.create() is only called here, guaranteeing the element is in the
  //    DOM and getComputedStyle() will work.
  const containerRef = useCallback((node: HTMLDivElement | null) => {
    if (!node) {
      // Node removed — clean up
      if (hmRef.current) {
        node?.replaceChildren(); // noop here, but kept for symmetry
      }
      hmRef.current = null;
      return;
    }

    // Node mounted — create the heatmap instance
    node.innerHTML = "";   // clear any leftover canvas from HMR / StrictMode

    hmRef.current = h337.create({
      container:  node,
      radius:     radiusRef.current,
      maxOpacity: opacityRef.current,
      minOpacity: 0,
      blur:       0.85,
      gradient:   gradientRef.current,
    });

    // Feed initial data immediately so the heatmap is visible on first render
    feedData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // intentionally empty — we never want to recreate the instance

  // ── Helper: compute points and push them to the heatmap instance
  function feedData() {
    const hm = hmRef.current;
    if (!hm) return;

    const points = buildHeatmapData(
      playersRef.current,
      vpRef.current,
      timeRangeRef.current,
      playerIdsRef.current,
    );

    if (points.length === 0) {
      hm.setData({ max: 1, data: [] });
      return;
    }

    const max = maxValueRef.current ?? Math.max(...points.map((p) => p.value)) * 10;
    hm.setData({ max, data: points });
  }

  // ── Recreate instance when visual config (radius / opacity / gradient) changes
  //    We do this by imperatively destroying + recreating via the stored node.
  const nodeRef = useRef<HTMLDivElement | null>(null);

  const stableContainerRef = useCallback((node: HTMLDivElement | null) => {
    nodeRef.current = node;
    containerRef(node);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef]);

  useLayoutEffect(() => {
    const node = nodeRef.current;
    if (!node) return;
    // Recreate heatmap with new config
    node.innerHTML = "";
    hmRef.current = h337.create({
      container:  node,
      radius,
      maxOpacity: opacity,
      minOpacity: 0,
      blur:       0.85,
      gradient,
    });
    feedData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [radius, opacity, JSON.stringify(gradient)]);

  // ── Update data whenever players / viewport / filters change
  useEffect(() => {
    feedData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [players, vp, timeRange, playerIds, maxValue]);

  return (
    <div
      ref={stableContainerRef}
      className={className}
      style={{
        position: "absolute",
        inset: 0,
        width,
        height,
        pointerEvents: "none",   // let mouse events pass through to Konva
        ...style,
      }}
    />
  );
}

// ─── HOOK (headless alternative) ─────────────────────────────────────────────

/**
 * Lower-level hook for cases where you manage the heatmap.js instance yourself
 * (e.g. rendering onto an existing canvas or a custom container).
 *
 * Returns the pre-projected data points ready to pass to hm.setData().
 */
export function useHeatmapData(
  players: ProcessedPlayer[],
  timeRange?: { from: number; to: number },
  playerIds?: Array<string | number>,
): HeatmapDataPoint[] {
  const vp = useViewport();

  return useMemo(
    () => buildHeatmapData(players, vp, timeRange, playerIds),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [players, vp, JSON.stringify(timeRange), JSON.stringify(playerIds)],
  );
}
